import { calendarDateFromHash } from '@util/CalendarEmbed.utilities';
import Utilities from '@util/Utilities';
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import CalendarMonth from '@app/state/calendar/CalendarMonth';
import MarketStateSnapshotOutput from '@app/model/market_state/output/MarketStateSnapshotOutput';
import MarketStateService from '@app/model/market_state/service/MarketStateService';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useMarketStateForPeriodQuery } from '@app/model/market_state/service/MarketStateSnapshotServiceHooks';
import YearMonth from '@app/state/calendar/YearMonth';
import MarketStateFilterInput from '@app/model/market_state/input/MarketStateFilterInput';
import { MarketStateGraphQLQueryKeys } from '@app/model/market_state/service/MarketStateGraphQLQueryKeys';
import MarketStateSnapshotTools from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateSnapshotTools';
import { EmptyCalendarPlaceholder } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_calendar/_desktop/fragment/empty/EmptyCalendarPlaceholder';
import { EmptyTablePlaceholder } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/EmptyTablePlaceholder';
import { EmptyGraphPlaceholder } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_graph/fragment/EmptyGraphPlaceholder';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import MarketStateDailySnapshotState from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDailySnapshotState';
import MarketStateDayNotificationsDrawer from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/MarketStateDayNotificationsDrawer';
import { matchPath } from 'react-router-dom';

const calendarMonthOperationSignature = (companyHotel: CompanyHotel) => {
	const operation = companyHotel.settings.operation;
	return `${operation.type}.${operation.seasonFrom ?? 'none'}.${operation.seasonTo ?? 'none'}`;
};

const calendarMonthStorageKey = (companyHotel: CompanyHotel) =>
	`roompulse.market-state.calendar-month.${companyHotel.id}.${calendarMonthOperationSignature(
		companyHotel
	)}`;

const loadCalendarMonth = (companyHotel: CompanyHotel): CalendarMonth => {
	const fallback = CalendarMonth.now(companyHotel.settings.operation);

	if (typeof window === 'undefined') {
		return fallback;
	}

	const linkedDate = calendarDateFromHash(window.location.hash, companyHotel.id);
	if (linkedDate) return fallback.getCalendarMonthAt({
		year: Number(linkedDate.slice(0, 4)), month: Utilities.getMonthByIndex(Number(linkedDate.slice(5, 7)) - 1),
	});

	try {
		const stored = window.sessionStorage.getItem(calendarMonthStorageKey(companyHotel));
		if (!stored) return fallback;

		const yearMonth = JSON.parse(stored) as Partial<YearMonth>;
		if (!yearMonth?.year || !yearMonth?.month) return fallback;

		return fallback.getCalendarMonthAt({ year: yearMonth.year, month: yearMonth.month });
	} catch {
		return fallback;
	}
};

export const MarketStateToolsContext = React.createContext<MarketStateSnapshotTools | undefined>(
	undefined
);

const MarketStateToolsProvider: React.FunctionComponent<
	PropsWithChildren<{
		companyHotel: CompanyHotel;
	}>
> = ({ children, companyHotel }) => {
	const isTable = !!matchPath('/secured/rateshopper/rates/market/table', location.pathname);
	const isGraph = !!matchPath('/secured/rateshopper/rates/market/graph', location.pathname);

	const operationSignature = calendarMonthOperationSignature(companyHotel);
	const calendarMonthContext = useRef({ companyHotelId: companyHotel.id, operationSignature });
	const skipNextCalendarMonthPersist = useRef(false);
	const [calendarMonth, setCalendarMonth] = useState<CalendarMonth>(() =>
		loadCalendarMonth(companyHotel)
	);

	useEffect(() => {
		const contextChanged =
			calendarMonthContext.current.companyHotelId !== companyHotel.id ||
			calendarMonthContext.current.operationSignature !== operationSignature;

		if (contextChanged) {
			skipNextCalendarMonthPersist.current = true;
			calendarMonthContext.current = { companyHotelId: companyHotel.id, operationSignature };
			setCalendarMonth(loadCalendarMonth(companyHotel));
		}
	}, [companyHotel.id, operationSignature]);

	useEffect(() => {
		if (skipNextCalendarMonthPersist.current) {
			skipNextCalendarMonthPersist.current = false;
			return;
		}

		try {
			window.sessionStorage.setItem(
				calendarMonthStorageKey(companyHotel),
				JSON.stringify(calendarMonth.index)
			);
		} catch {
			// Ignore storage failures so calendar navigation remains usable.
		}
	}, [companyHotel.id, operationSignature, calendarMonth.index.year, calendarMonth.index.month]);

	const nextCalendarMonth = useCallback(() => {
		setCalendarMonth(calendarMonth.getNextCalendarMonth());
	}, [calendarMonth]);

	const previousCalendarMonth = useCallback(() => {
		setCalendarMonth(calendarMonth.getPreviousCalendarMonth());
	}, [calendarMonth]);

	const atCalendarMonth = useCallback(
		(yearMonth: YearMonth) => {
			setCalendarMonth(calendarMonth.getCalendarMonthAt(yearMonth));
		},
		[calendarMonth]
	);

	let placeholder = <EmptyCalendarPlaceholder />;
	if (isTable) placeholder = <EmptyTablePlaceholder />;
	else if (isGraph) placeholder = <EmptyGraphPlaceholder />;

	if (calendarMonth) {
		return (
			<InitializeMarketStateSnapshot
				companyHotel={companyHotel}
				calendarMonth={calendarMonth}
				loadingPlaceholder={placeholder}
				nextCalendarMonth={nextCalendarMonth}
				previousCalendarMonth={previousCalendarMonth}
				setCalendarMonth={atCalendarMonth}
			>
				{children}
			</InitializeMarketStateSnapshot>
		);
	} else return <></>;
};

const InitializeMarketStateSnapshot: React.FunctionComponent<
	PropsWithChildren<{
		calendarMonth: CalendarMonth;
		companyHotel: CompanyHotel;
		loadingPlaceholder: React.ReactNode;
		nextCalendarMonth: () => void;
		previousCalendarMonth: () => void;
		setCalendarMonth: (yearMonth: YearMonth) => void;
	}>
> = ({
	children,
	calendarMonth,
	companyHotel,
	loadingPlaceholder,
	nextCalendarMonth,
	previousCalendarMonth,
	setCalendarMonth,
}) => {
	const { filter } = useMarketStateFilterInputTools();

	const marketStateQuery = useMarketStateForPeriodQuery(
		companyHotel.id,
		calendarMonth.index.year,
		calendarMonth.index.month,
		calendarMonth.range.startStatDate,
		calendarMonth.range.endStatDate,
		filter
	);

	if (marketStateQuery.isLoading) return loadingPlaceholder;

	if (marketStateQuery.isSuccess && !!marketStateQuery.data) {
		return (
			<RenderMarketStateSnapshot
				calendarMonth={calendarMonth}
				companyHotel={companyHotel}
				marketStateSnapshot={marketStateQuery.data}
				nextCalendarMonth={nextCalendarMonth}
				previousCalendarMonth={previousCalendarMonth}
				setCalendarMonth={setCalendarMonth}
			>
				{children}
			</RenderMarketStateSnapshot>
		);
	}
	return <></>;
};

const RenderMarketStateSnapshot: React.FunctionComponent<
	PropsWithChildren<{
		calendarMonth: CalendarMonth;
		companyHotel: CompanyHotel;
		marketStateSnapshot: MarketStateSnapshotOutput;
		nextCalendarMonth: () => void;
		previousCalendarMonth: () => void;
		setCalendarMonth: (yearMonth: YearMonth) => void;
	}>
> = ({
	children,
	calendarMonth,
	companyHotel,
	marketStateSnapshot,
	nextCalendarMonth,
	previousCalendarMonth,
	setCalendarMonth,
}) => {
	const notificationsDrawer = useRef<{
		open: (alertExecutionId: number, statDate: string) => void;
	}>(null);
	const clearUpdateFinishedTimer = useRef<number | undefined>(undefined);
	const openNotificationsDrawer = useCallback((alertExecutionId: number, statDate: string) => {
		if (notificationsDrawer.current) notificationsDrawer.current!.open(alertExecutionId, statDate);
	}, []);

	const toaster = useToaster();
	const { filter } = useMarketStateFilterInputTools();
	const queryClient = useQueryClient();

	const loadDailySnapshotStates = useCallback(
		(marketStateSnapshot: MarketStateSnapshotOutput) => {
			return calendarMonth.days.map(day => {
				const dailySnapshot = marketStateSnapshot.findDailySnapshot(day.display);
				return new MarketStateDailySnapshotState(day, dailySnapshot);
			});
		},
		[calendarMonth.days]
	);

	const [marketStateDailySnapshots, setMarketStateDailySnapshots] = useState<
		MarketStateDailySnapshotState[]
	>(loadDailySnapshotStates(marketStateSnapshot));

	useEffect(() => {
		setMarketStateDailySnapshots(loadDailySnapshotStates(marketStateSnapshot));
	}, [calendarMonth, filter, marketStateSnapshot]);

	useEffect(() => {
		return () => {
			if (clearUpdateFinishedTimer.current) {
				window.clearTimeout(clearUpdateFinishedTimer.current);
			}
		};
	}, []);

	const findDailySnapshotState = useCallback(
		(statDate: string) => {
			return marketStateDailySnapshots.find(snapshot => snapshot.calendarDay.display === statDate);
		},
		[marketStateDailySnapshots]
	);

	const triggerMarketStateUpdateMutation = useMutation({
		mutationFn: MarketStateService.triggerMarketStateUpdate,
		onSuccess: () => {
			const snapshots = marketStateDailySnapshots.map(snapshot => {
				const newSnapshot = new MarketStateDailySnapshotState(
					snapshot.calendarDay,
					snapshot.marketStateDailySnapshot
				);
				newSnapshot.isUpdating =
					!snapshot.calendarDay.isPast && snapshot.calendarDay.isCurrentMonth;
				return newSnapshot;
			});
			setMarketStateDailySnapshots(snapshots);
		},
		onError: () => {
			toaster.error(
				'Dashboard',
				'Calendar could not be refreshed at the moment. Please try again later!'
			);
		},
	});

	const triggerPriceRecommendationUpdateMutation = useMutation({
		mutationFn: MarketStateService.triggerPriceRecommendationUpdate,
		onSuccess: () => {
			toggleMarketStateIsUpdating(true);
		},
		onError: () => {
			toaster.error(
				'Dashboard',
				'Calendar could not be refreshed at the moment. Please try again later!'
			);
		},
	});

	const toggleMarketStateIsUpdating = useCallback((isUpdating?: boolean) => {
		setMarketStateDailySnapshots(prevSnapshots =>
			prevSnapshots.map(snapshot => {
				const newSnapshot = new MarketStateDailySnapshotState(
					snapshot.calendarDay,
					snapshot.marketStateDailySnapshot
				);
				newSnapshot.isUpdating = isUpdating
					? !snapshot.calendarDay.isPast && snapshot.calendarDay.isCurrentMonth
					: false;
				newSnapshot.updateFinished = !isUpdating && snapshot.isUpdating;
				return newSnapshot;
			})
		);

		if (!isUpdating) {
			if (clearUpdateFinishedTimer.current) {
				window.clearTimeout(clearUpdateFinishedTimer.current);
			}
			clearUpdateFinishedTimer.current = window.setTimeout(() => {
				setMarketStateDailySnapshots(prevSnapshots =>
					prevSnapshots.map(snapshot => {
						const newSnapshot = new MarketStateDailySnapshotState(
							snapshot.calendarDay,
							snapshot.marketStateDailySnapshot
						);
						newSnapshot.isUpdating = snapshot.isUpdating;
						newSnapshot.updateFinished = false;
						return newSnapshot;
					})
				);
				clearUpdateFinishedTimer.current = undefined;
			}, 3000);
		}
	}, []);

	const triggerPriceRecommendationLiteUpdateMutation = useMutation({
		mutationFn: MarketStateService.triggerPriceRecommendationLiteUpdate,
		onSuccess: () => {
			toggleMarketStateIsUpdating(true);
		},
		onError: () => {
			toaster.error(
				'Dashboard',
				'Calendar could not be refreshed at the moment. Please try again later!'
			);
		},
	});

	const { mutateAsync: triggerMarketStateUpdateMutateAsync } = triggerMarketStateUpdateMutation;
	const { mutateAsync: triggerPriceRecommendationUpdateMutateAsync } =
		triggerPriceRecommendationUpdateMutation;
	const { mutateAsync: triggerPriceRecommendationLiteUpdateMutateAsync } =
		triggerPriceRecommendationLiteUpdateMutation;

	const triggerMarketStateUpdate = useCallback(
		(numberOfMonths: number, withOta: boolean, filter: MarketStateFilterInput, withProof = false) => {
			return triggerMarketStateUpdateMutateAsync({
				companyHotelId: companyHotel.id,
				year: calendarMonth.index.year,
				month: calendarMonth.index.month,
				numberOfMonths: numberOfMonths,
				filterInput: filter,
				withOta: withOta,
				withProof: withProof,
			});
		},
		[
			calendarMonth.index.month,
			calendarMonth.index.year,
			companyHotel.id,
			triggerMarketStateUpdateMutateAsync,
		]
	);

	const triggerPriceRecommendationUpdate = useCallback(() => {
		return triggerPriceRecommendationUpdateMutateAsync({
			companyHotelId: companyHotel.id,
			year: calendarMonth.index.year,
			month: calendarMonth.index.month,
		});
	}, [
		calendarMonth.index.month,
		calendarMonth.index.year,
		companyHotel.id,
		triggerPriceRecommendationUpdateMutateAsync,
	]);

	const triggerPriceRecommendationLiteUpdate = useCallback(() => {
		return triggerPriceRecommendationLiteUpdateMutateAsync({
			companyHotelId: companyHotel.id,
			year: calendarMonth.index.year,
			month: calendarMonth.index.month,
		});
	}, [
		calendarMonth.index.month,
		calendarMonth.index.year,
		companyHotel.id,
		triggerPriceRecommendationLiteUpdateMutateAsync,
	]);

	const reloadMarketStateForDay = useCallback(
		(statDate: string) => {
			MarketStateService.findMarketStateForDay({
				companyHotelId: companyHotel.id,
				statDate: statDate,
				filterInput: filter,
			}).then(newSnapshot => {
				if (newSnapshot) {
					setMarketStateDailySnapshots(prevSnapshots => {
						const snapshots = [...prevSnapshots];
						const index = snapshots.findIndex(
							snapshot => snapshot.calendarDay.display === newSnapshot.statDate
						);
						if (index > -1) {
							console.log('reloadMarketStateForDay', newSnapshot);
							const newDailyState = new MarketStateDailySnapshotState(
								snapshots[index].calendarDay,
								newSnapshot
							);
							newDailyState.updateFinished = true;
							newDailyState.isUpdating = false;
							snapshots[index] = newDailyState;
						}
						return snapshots;
					});
				}
			});
		},
		[companyHotel.id, filter]
	);

	const findPreviousSnapshot = useCallback(
		async (statDate: string, snapshotLatestUpdateDate: string) => {
			if (companyHotel.settings.calendar.numberOfDaysForPriceParity) {
				try {
					const previousSnapshot = await queryClient.fetchQuery({
						queryKey: [...MarketStateGraphQLQueryKeys.previous(companyHotel.id, statDate, filter), snapshotLatestUpdateDate],
						staleTime: Infinity,
						queryFn: async () => {
							const previousSnapshot = await MarketStateService.findPreviousMarketStateForDay({
								companyHotelId: companyHotel.id,
								statDate: statDate,
								filterInput: filter,
								fromDate: snapshotLatestUpdateDate,
							});
							return previousSnapshot ?? null;
						},
					});
					return previousSnapshot ?? undefined;
				} catch (error) {
					return undefined;
				}
			}
			return undefined;
		},
		[
			companyHotel.id,
			companyHotel.settings.calendar.numberOfDaysForPriceParity,
			filter,
			queryClient,
		]
	);

	const tools: MarketStateSnapshotTools = useMemo(
		() => ({
			calendarMonth: calendarMonth,
			companyHotel: companyHotel,
			marketStateSnapshot: marketStateSnapshot,
			marketStateDailySnapshotStates: marketStateDailySnapshots,
			findDailySnapshotState: findDailySnapshotState,
			openNotificationsDrawer: openNotificationsDrawer,
			triggerMarketStateUpdate: triggerMarketStateUpdate,
			triggerPriceRecommendationUpdate: triggerPriceRecommendationUpdate,
			triggerPriceRecommendationLiteUpdate: triggerPriceRecommendationLiteUpdate,
			nextCalendarMonth: nextCalendarMonth,
			previousCalendarMonth: previousCalendarMonth,
			setCalendarMonth: setCalendarMonth,
			reloadMarketStateForDay: reloadMarketStateForDay,
			findPreviousSnapshot: findPreviousSnapshot,
			toggleMarketStateIsUpdating: toggleMarketStateIsUpdating,
		}),
		[
			calendarMonth,
			companyHotel,
			findDailySnapshotState,
			findPreviousSnapshot,
			marketStateDailySnapshots,
			marketStateSnapshot,
			nextCalendarMonth,
			openNotificationsDrawer,
			previousCalendarMonth,
			reloadMarketStateForDay,
			setCalendarMonth,
			toggleMarketStateIsUpdating,
			triggerMarketStateUpdate,
			triggerPriceRecommendationLiteUpdate,
			triggerPriceRecommendationUpdate,
		]
	);

	return (
		<MarketStateToolsContext.Provider value={tools}>
			<MarketStateDayNotificationsDrawer ref={notificationsDrawer} />
			{children}
		</MarketStateToolsContext.Provider>
	);
};

export default MarketStateToolsProvider;
